/**
 * Product analytics: Umami (cookieless page counts and named events) and
 * Microsoft Clarity (session replays with typing hidden).
 *
 * Both load only in a production build. Their ids are gardengameplan.com's
 * own (owner, 2026-10-13: "im only allowed one site so we are just reusing
 * the garden gameplan ids"; ANALYTICS_UMAMI_WEBSITE_ID and
 * ANALYTICS_CLARITY_PROJECT_ID), so Grow's visits sit in the website's
 * Umami and Clarity, told apart by the grow.gardengameplan.com hostname. The
 * build variables VITE_UMAMI_WEBSITE_ID and VITE_CLARITY_PROJECT_ID, when
 * set, take their place. Neither id is a secret: both are public once built.
 *
 * Who uses it: main.tsx (initAnalytics), the route listener in App.tsx
 * (trackPageView, through AnalyticsRouteTracker), and the api modules that
 * name an event (users, plantCatalogue, packets, tray, photos, diagnose,
 * shares, billing) plus SubscribeSuccess.
 *
 * Privacy: events carry only the event name and, at most, a small count or a
 * word from a fixed list. Never a name, an email, a ZIP or anything a gardener
 * wrote. Page views send the path only (no query string, which can hold a
 * checkout session id or a founding code). Clarity gets every input, textarea,
 * select and editable field masked, plus anything marked data-clarity-mask.
 *
 * Surprising: Umami is loaded with data-auto-track="false" so the app reports
 * its own route changes (a single-page app would otherwise be counted once).
 *
 * @module analytics
 */

import { ANALYTICS_CLARITY_PROJECT_ID, ANALYTICS_UMAMI_WEBSITE_ID } from '@/config/tunables/shared';

/** Umami's hosted script. */
const UMAMI_SCRIPT_URL = 'https://cloud.umami.is/script.js';

/** Clarity's tag script, the project id added to the end. */
const CLARITY_SCRIPT_BASE = 'https://www.clarity.ms/tag/';

/** The events the app reports. Nothing else is sent. */
export type AnalyticsEvent =
  | 'sign_up'
  | 'plant_added'
  | 'photos_added'
  | 'packet_scan'
  | 'checkup'
  | 'share'
  | 'subscribe_started'
  | 'subscribed'
  | 'founding_claimed';

/** Extra values on an event: counts or words from a fixed list, never personal text. */
export type AnalyticsData = Record<string, string | number | boolean>;

/** Umami's global tracker. */
type UmamiTracker = {
  track: (arg: string | ((props: Record<string, unknown>) => Record<string, unknown>), data?: Record<string, unknown>) => void;
};

/** Clarity's global function (a queue until its script has loaded). */
type ClarityFn = (...args: unknown[]) => void;

/** The window with the two trackers, once they have loaded. */
type TrackerWindow = Window & { umami?: UmamiTracker; clarity?: ClarityFn };

/** The Umami website id: the build's, else gardengameplan.com's. */
const UMAMI_WEBSITE_ID: string = (import.meta.env.VITE_UMAMI_WEBSITE_ID || ANALYTICS_UMAMI_WEBSITE_ID).trim();

/** The Clarity project id: the build's, else gardengameplan.com's. */
const CLARITY_PROJECT_ID: string = (import.meta.env.VITE_CLARITY_PROJECT_ID || ANALYTICS_CLARITY_PROJECT_ID).trim();

/** Whether this is a production build: the trackers never load in development. */
const IS_PRODUCTION: boolean = import.meta.env.PROD === true;

/** Whether initAnalytics has already run, so a second call adds nothing. */
let started = false;

/**
 * Adds a script to the page.
 *
 * @param src - The script's address.
 * @param attributes - Attributes to set on the tag.
 */
function addScript(src: string, attributes: Record<string, string>): void {
  const tag = document.createElement('script');
  tag.async = true;
  tag.src = src;
  for (const [name, value] of Object.entries(attributes)) tag.setAttribute(name, value);
  document.head.appendChild(tag);
}

/**
 * Marks the fields a replay must never show: every input, textarea, select
 * and editable area, now and as the app draws more. Clarity shows a masked
 * element as blocks instead of its text.
 */
function maskFormFields(): void {
  const selector = 'input, textarea, select, [contenteditable="true"]';
  /** Marks each form field under a root as masked for Clarity. */
  const mark = (root: ParentNode): void => {
    root.querySelectorAll(selector).forEach((el) => {
      if (!el.hasAttribute('data-clarity-mask')) el.setAttribute('data-clarity-mask', 'true');
    });
  };
  mark(document);
  new MutationObserver((records) => {
    for (const record of records) {
      record.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(selector)) node.setAttribute('data-clarity-mask', 'true');
        mark(node);
      });
    }
  }).observe(document.body, { childList: true, subtree: true });
}

/**
 * Loads Umami and Clarity, each only when its variable is set and the build
 * is a production one. Safe to call twice.
 */
export function initAnalytics(): void {
  if (started || !IS_PRODUCTION || typeof document === 'undefined') return;
  started = true;

  if (UMAMI_WEBSITE_ID) {
    addScript(UMAMI_SCRIPT_URL, { 'data-website-id': UMAMI_WEBSITE_ID, 'data-auto-track': 'false' });
  }

  if (CLARITY_PROJECT_ID) {
    const w = window as TrackerWindow;
    w.clarity = w.clarity || function (...args: unknown[]) {
      const queued = w.clarity as ClarityFn & { q?: unknown[][] };
      (queued.q = queued.q || []).push(args);
    };
    addScript(`${CLARITY_SCRIPT_BASE}${CLARITY_PROJECT_ID}`, {});
    maskFormFields();
  }
}

/**
 * Counts a page view. The path only: a query string can hold a checkout
 * session id or a founding code, so it is left off.
 *
 * @param pathname - The route's path, e.g. /plants.
 */
export function trackPageView(pathname: string): void {
  if (!UMAMI_WEBSITE_ID || !IS_PRODUCTION || typeof window === 'undefined') return;
  try {
    (window as TrackerWindow).umami?.track((props) => ({ ...props, url: pathname, title: document.title }));
  } catch {
    // Analytics must never break the app.
  }
}

/**
 * Reports a named event to Umami, and to Clarity as a custom event. Does
 * nothing when neither is loaded.
 *
 * @param name - One of the fixed event names.
 * @param data - Counts or fixed words only; never personal text.
 */
export function trackEvent(name: AnalyticsEvent, data?: AnalyticsData): void {
  if (!IS_PRODUCTION || typeof window === 'undefined') return;
  try {
    const w = window as TrackerWindow;
    if (UMAMI_WEBSITE_ID) w.umami?.track(name, data);
    if (CLARITY_PROJECT_ID && typeof w.clarity === 'function') w.clarity('event', name);
  } catch {
    // Analytics must never break the app.
  }
}
